/**
 * Color Service - Centralized color coding logic for match statuses
 * This service handles all color-related logic to prevent inconsistencies
 * and make the system more maintainable.
 */

// console.log('🎨 colorService.js loaded - VERSION WITH CENTRALIZED LOGIC');

// Color definitions for match statuses
export const MATCH_COLORS = {
  HIGH: {
    backgroundColor: '#22c55e', // Green (Grid View green)
    textColor: 'white',
    borderColor: '#16a34a'
  },
  MEDIUM: {
    backgroundColor: '#f59e0b', // Orange
    textColor: 'white', 
    borderColor: '#d97706'
  },
  LOW: {
    backgroundColor: '#ef4444', // Red
    textColor: 'white',
    borderColor: '#dc2626'
  },
  NO_MATCH: {
    backgroundColor: '#ef4444', // Red (was gray - now matches Grid View logic)
    textColor: 'white',
    borderColor: '#dc2626'
  },
  NO_DATA: {
    backgroundColor: '#d1fae5', // Light green - for when both core and documents are "Not Found"
    textColor: '#000000', // Black text
    borderColor: '#6ee7b7'
  },
  // Legacy status names for backward compatibility
  PERFECT_MATCH: {
    backgroundColor: '#22c55e', // Use same green as Grid View
    textColor: 'white',
    borderColor: '#16a34a'
  },
  MAYBE_MATCH: {
    backgroundColor: '#f59e0b',
    textColor: 'white',
    borderColor: '#d97706'
  },
  MATCH: {
    backgroundColor: '#22c55e', // Use same green as Grid View
    textColor: 'white',
    borderColor: '#16a34a'
  }
};

// Display status colors (used after getDisplayMatchStatus conversion)
export const DISPLAY_STATUS_COLORS = {
  perfect: {
    backgroundColor: '#22c55e', // Green (Grid View green)
    textColor: 'white',
    borderColor: '#16a34a'
  },
  matched: {
    backgroundColor: '#22c55e', // Green (alias for perfect)
    textColor: 'white',
    borderColor: '#16a34a'
  },
  maybe: {
    backgroundColor: '#f59e0b', // Orange
    textColor: 'white',
    borderColor: '#d97706'
  },
  mismatch: {
    backgroundColor: '#ef4444', // Red
    textColor: 'white',
    borderColor: '#dc2626'
  },
  no_data: {
    backgroundColor: '#d1fae5', // Light green
    textColor: '#000000', // Black text
    borderColor: '#6ee7b7'
  },
  unknown: {
    backgroundColor: '#d1d5db', // Light gray
    textColor: '#374151',
    borderColor: '#9ca3af'
  }
};

// Default colors for unknown statuses
export const DEFAULT_COLORS = {
  backgroundColor: '#d1d5db',
  textColor: '#374151',
  borderColor: '#9ca3af'
};

/**
 * Get colors for a match status
 * @param {string} status - The match status (HIGH, MEDIUM, LOW, NO_MATCH, etc.)
 * @returns {object} Color object with backgroundColor, textColor, and borderColor
 */
export const getMatchStatusColors = (status) => {
  if (!status) {
    return DEFAULT_COLORS;
  }
  
  const normalizedStatus = status.toUpperCase();
  return MATCH_COLORS[normalizedStatus] || DEFAULT_COLORS;
};

/**
 * Get background color for a match status
 * @param {string} status - The match status
 * @returns {string} Background color hex code
 */
export const getMatchStatusBackgroundColor = (status) => {
  return getMatchStatusColors(status).backgroundColor;
};

/**
 * Get text color for a match status
 * @param {string} status - The match status
 * @returns {string} Text color hex code
 */
export const getMatchStatusTextColor = (status) => {
  return getMatchStatusColors(status).textColor;
};

/**
 * Get border color for a match status
 * @param {string} status - The match status
 * @returns {string} Border color hex code
 */
export const getMatchStatusBorderColor = (status) => {
  return getMatchStatusColors(status).borderColor;
};

/**
 * Get complete style object for a match status
 * @param {string} status - The match status
 * @returns {object} Complete style object with background, color, and border
 */
export const getMatchStatusStyles = (status) => {
  const colors = getMatchStatusColors(status);
  return {
    backgroundColor: colors.backgroundColor,
    color: colors.textColor,
    borderColor: colors.borderColor,
    border: `1px solid ${colors.borderColor}`
  };
};

/**
 * CENTRALIZED MATCH STATUS LOGIC
 * Converts database match status to display status (Grid View logic)
 * This ensures consistent behavior across Case List and Grid View
 * 
 * @param {object} fieldData - The field data object with value, master_value, system_document_match
 * @returns {string} Display status: 'perfect', 'maybe', 'mismatch', 'no_data', or 'unknown'
 */
export const getDisplayMatchStatus = (fieldData) => {
  if (!fieldData) return 'no_data';
  
  // Check for override first
  if (fieldData.match_status_override) {
    return fieldData.match_status_override;
  }
  
  // Helper to check if a value is "not found" or empty
  const isNotFound = (val) => {
    if (!val) return true; // null, undefined, empty string
    const normalized = String(val).toLowerCase().trim();
    return normalized === '' || normalized === 'not found' || normalized === 'n/a';
  };
  
  // Check for "No Data" scenario FIRST: both values are missing/not found
  // This takes precedence over system_document_match
  const extracted = fieldData.value;
  const core = fieldData.master_value || fieldData.core_system_value;
  
  if (isNotFound(extracted) && isNotFound(core)) {
    return 'no_data'; // GRAY - both values missing
  }
  
  // Use system_document_match from database
  const dbStatus = fieldData.system_document_match || fieldData[`system_document_match`];
  
  if (dbStatus) {
    switch (dbStatus.toUpperCase()) {
      // Database format
      case 'HIGH':
      case 'PERFECT_MATCH':
        return 'perfect';
      case 'MEDIUM':
      case 'MAYBE_MATCH':
        return 'maybe';
      case 'LOW':
      case 'NO_MATCH':
        return 'mismatch'; // RED - mismatch (one has value, other doesn't, or values don't match)
      // Override format (saved as display values)
      case 'PERFECT':
      case 'MATCHED':
        return 'perfect';
      case 'MAYBE':
        return 'maybe';
      case 'MISMATCH':
        return 'mismatch';
      default:
        return 'unknown';
    }
  }
  return 'unknown';
};

/**
 * Get match status from various possible field names
 * This handles different data structures and field naming conventions
 * @param {object} row - The data row
 * @param {string} fieldName - The field name
 * @returns {string} The match status
 */
export const getMatchStatusFromRow = (row, fieldName) => {
  if (!row || !fieldName) {
    return 'NO_MATCH';
  }
  
  // Try different possible field naming conventions
  const possibleKeys = [
    `${fieldName}_match`,           // Standard case list format
    `${fieldName}_system_document_match`, // Alternative format
    `${fieldName}_document_match`,  // Document match format
    'system_document_match',        // Direct field (for single field views)
    'document_match'                // Direct field (for single field views)
  ];
  
  // Look for the first available match status
  for (const key of possibleKeys) {
    if (row[key] && row[key] !== '') {
      return row[key];
    }
  }
  
  return 'NO_MATCH';
};

/**
 * Get colors for a field in a row
 * This is the main function that should be used in components
 * @param {object} row - The data row
 * @param {string} fieldName - The field name
 * @returns {object} Color object with backgroundColor, textColor, and borderColor
 */
export const getFieldColors = (row, fieldName) => {
  const matchStatus = getMatchStatusFromRow(row, fieldName);
  return getMatchStatusColors(matchStatus);
};

/**
 * Get background color for a field in a row
 * @param {object} row - The data row
 * @param {string} fieldName - The field name
 * @returns {string} Background color hex code
 */
export const getFieldBackgroundColor = (row, fieldName) => {
  return getFieldColors(row, fieldName).backgroundColor;
};

/**
 * Get text color for a field in a row
 * @param {object} row - The data row
 * @param {string} fieldName - The field name
 * @returns {string} Text color hex code
 */
export const getFieldTextColor = (row, fieldName) => {
  return getFieldColors(row, fieldName).textColor;
};

/**
 * Get complete style object for a field in a row
 * @param {object} row - The data row
 * @param {string} fieldName - The field name
 * @returns {object} Complete style object
 */
export const getFieldStyles = (row, fieldName) => {
  const matchStatus = getMatchStatusFromRow(row, fieldName);
  return getMatchStatusStyles(matchStatus);
};

/**
 * Check if a field has a match (any status other than NO_MATCH)
 * @param {object} row - The data row
 * @param {string} fieldName - The field name
 * @returns {boolean} True if the field has a match
 */
export const hasMatch = (row, fieldName) => {
  const matchStatus = getMatchStatusFromRow(row, fieldName);
  return matchStatus !== 'NO_MATCH';
};

/**
 * Check if a field has a high confidence match
 * @param {object} row - The data row
 * @param {string} fieldName - The field name
 * @returns {boolean} True if the field has a high confidence match
 */
export const hasHighMatch = (row, fieldName) => {
  const matchStatus = getMatchStatusFromRow(row, fieldName);
  return matchStatus === 'HIGH' || matchStatus === 'PERFECT_MATCH' || matchStatus === 'MATCH';
};

/**
 * Get a human-readable description of the match status
 * @param {string} status - The match status
 * @returns {string} Human-readable description
 */
export const getMatchStatusDescription = (status) => {
  const descriptions = {
    HIGH: 'High Confidence Match',
    MEDIUM: 'Medium Confidence Match', 
    LOW: 'Low Confidence Match',
    NO_MATCH: 'No Match Found',
    PERFECT_MATCH: 'Perfect Match',
    MAYBE_MATCH: 'Possible Match',
    MATCH: 'Match Found'
  };
  
  return descriptions[status?.toUpperCase()] || 'Unknown Status';
};

/**
 * Get colors for a display status (perfect, maybe, mismatch, no_data)
 * @param {string} displayStatus - The display status
 * @returns {object} Color object with backgroundColor, textColor, and borderColor
 */
export const getDisplayStatusColors = (displayStatus) => {
  if (!displayStatus) {
    return DEFAULT_COLORS;
  }
  
  return DISPLAY_STATUS_COLORS[displayStatus] || DEFAULT_COLORS;
};

/**
 * Get background color for a display status
 * @param {string} displayStatus - The display status
 * @returns {string} Background color hex code
 */
export const getDisplayStatusBackgroundColor = (displayStatus) => {
  return getDisplayStatusColors(displayStatus).backgroundColor;
};

/**
 * CENTRALIZED FIELD COLOR FUNCTION FOR CASE LIST
 * Converts field data to colors using Grid View logic
 * @param {object} row - The data row
 * @param {string} fieldName - The field name
 * @returns {string} Background color hex code
 */
export const getFieldBackgroundColorCentralized = (row, fieldName) => {
  if (!row || !fieldName) {
    return DEFAULT_COLORS.backgroundColor;
  }
  
  // Build field data object from row
  const fieldData = {
    field_name: fieldName,
    value: row[fieldName],
    master_value: row[`${fieldName}_master`],
    system_document_match: row[`${fieldName}_match`],
    match_status_override: Array.isArray(row._matchStatusOverrides) 
      ? row._matchStatusOverrides.find(o => o.field_name === fieldName)?.match_status 
      : undefined
  };
  
  // Use centralized logic
  const displayStatus = getDisplayMatchStatus(fieldData);
  
  return getDisplayStatusBackgroundColor(displayStatus);
};

export default {
  getMatchStatusColors,
  getMatchStatusBackgroundColor,
  getMatchStatusTextColor,
  getMatchStatusBorderColor,
  getMatchStatusStyles,
  getMatchStatusFromRow,
  getFieldColors,
  getFieldBackgroundColor,
  getFieldTextColor,
  getFieldStyles,
  hasMatch,
  hasHighMatch,
  getMatchStatusDescription,
  getDisplayMatchStatus,
  getDisplayStatusColors,
  getDisplayStatusBackgroundColor,
  getFieldBackgroundColorCentralized,
  MATCH_COLORS,
  DISPLAY_STATUS_COLORS,
  DEFAULT_COLORS
};
